"use client";

import { FC, ReactNode } from "react";

const BAR = "animate-pulse rounded bg-[rgb(229,229,229)]";

export interface StockListingPageSkeletonProps {
  /** Number of placeholder rows. Should match the server-rendered row count to avoid layout shift. */
  rowCount?: number;
  /** Reserves the desktop table header row (hidden on mobile, same as StockTable). */
  showTableHeader?: boolean;
  /** Static header markup rendered before the client listing hydrates. */
  headerSlot?: ReactNode;
  /** Static mobile controls rendered before the client listing hydrates. */
  mobileHeaderSlot?: ReactNode;
  /** Whether to reserve the Show More control below the initial rows. */
  showMore?: boolean;
}

/**
 * Suspense fallback for StockListingPageClient (PLP: all stocks, 52 week high/low, gainers/losers).
 * Mirrors the exact box model of the real listing (section paddings, 41.5px header row,
 * 72.5px data rows) so swapping the fallback for real content produces no layout shift.
 */
const StockListingPageSkeleton: FC<StockListingPageSkeletonProps> = ({
  rowCount = 10,
  showTableHeader = true,
  headerSlot,
  mobileHeaderSlot,
  showMore = false,
}) => (
  <div className="flex w-full">
    <aside className="hidden w-[16.5%] shrink-0 lg:block" />
    <section
      className="box-border block w-full max-w-223 border-l-[rgba(0,0,0,0.08)] bg-white leading-[19.2px] md:border-l-3 md:p-3"
      aria-hidden="true"
    >
      {headerSlot}
      {mobileHeaderSlot}
      <div className="w-full px-4 py-1">
        <div className="w-full max-w-217">
          {showTableHeader && (
            <div className="box-border hidden h-[41.5px] w-full items-center justify-between border-b border-[rgb(229,229,229)] md:flex">
              <span className={`${BAR} h-3.5 w-27.5`} />
              <span className={`${BAR} h-3.5 w-8`} />
            </div>
          )}

          {Array.from({ length: rowCount }, (_, index) => `stock-listing-skeleton-row-${index}`).map(
            (rowKey) => (
              <div
                key={rowKey}
                className="box-border flex h-[72.5px] w-full items-center justify-between gap-3 border-b border-b-[rgb(229,229,229)]"
              >
                <div className="flex min-w-0 flex-col items-start">
                  <span className={`${BAR} mb-1 block h-4.75 w-23`} />
                  <span className={`${BAR} block h-[16.5px] w-37.5`} />
                </div>

                <div className="flex flex-col items-end">
                  <span className={`${BAR} mb-1 block h-4.75 w-18`} />
                  <span className={`${BAR} block h-[16.5px] w-24`} />
                </div>

                <span className={`${BAR} ml-2 block h-5 w-5 shrink-0`} />
              </div>
            ),
          )}
        </div>
        {showMore && (
          <div className="mt-4 flex max-w-[850px] justify-center gap-4 p-4 pt-0">
            <span className={`${BAR} block h-10 w-full max-w-[258px]`} />
          </div>
        )}
      </div>
    </section>
  </div>
);

export default StockListingPageSkeleton;
